import React, { useState, useEffect } from 'react';
import { Coins, Check, ShoppingCart } from 'lucide-react';
import { useCurrencyStore } from '../../stores/useCurrencyStore';
import { useCartStore } from '../../stores/useCartStore';
import { useUIStore } from '../../stores/useUIStore';
import { getCrewPrice, setHasPurchasedCrewGlobal, formatPrice } from '../../utils/currency';

interface CrewCosmetic {
  name: string;
  image?: string | null;
  is_lego?: boolean;
}

interface CrewPackageState {
  skin_name: string;
  skin_image: string | null;
  extra_items: CrewCosmetic[];
}

export const RecargasPage: React.FC = () => {
  const currency = useCurrencyStore((s) => s.currency);
  const addItem = useCartStore((s) => s.addItem);
  const openCart = useUIStore((s) => s.openCart);

  const [addedCrew, setAddedCrew] = useState(false);
  const [addedPavos, setAddedPavos] = useState(false);
  const [hasPurchasedCrew, setHasPurchasedCrewState] = useState(false);
  const [crewInfo, setCrewInfo] = useState<CrewPackageState>({
    skin_name: 'Club de Fortnite',
    skin_image: null,
    extra_items: [],
  });

  useEffect(() => {
    const token =
      localStorage.getItem('token') ||
      localStorage.getItem('fn_token') ||
      localStorage.getItem('auth_token');

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Consultar dinámicamente la skin del mes y cosméticos oficiales del Club
    fetch('/api/recharges/info', { headers })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data && data.crew_package) {
          const cp = data.crew_package;
          setCrewInfo({
            skin_name: cp.skin_name || 'Club de Fortnite',
            skin_image: cp.skin_image || null,
            extra_items: Array.isArray(cp.extra_items) ? cp.extra_items : [],
          });
          if (typeof cp.is_first_purchase === 'boolean') {
            setHasPurchasedCrewState(!cp.is_first_purchase);
            setHasPurchasedCrewGlobal(!cp.is_first_purchase);
          }
        }
      })
      .catch(() => {});

    if (token) {
      fetch('/api/auth/me/has-purchased-crew', {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (data && typeof data.has_purchased_crew === 'boolean') {
            setHasPurchasedCrewState(data.has_purchased_crew);
            setHasPurchasedCrewGlobal(data.has_purchased_crew);
          }
        })
        .catch(() => {});
    }
  }, []);

  const crewFiatNum = getCrewPrice(currency, hasPurchasedCrew);

  const handleAddCrew = () => {
    const skinImg = crewInfo.skin_image || 'https://fortnite-api.com/images/vbuck.png';
    addItem({
      id: 'recharge_crew',
      offerId: 'recharge_crew',
      name: 'Club de Fortnite (Crew)',
      vbucks: 800,
      finalPrice: crewFiatNum,
      regularPrice: crewFiatNum,
      image: skinImg,
      images: {
        icon: skinImg,
        featured: skinImg,
      },
      category: 'Recargas',
      rarity: 'Epic',
      isBundle: false,
    });
    setAddedCrew(true);
    setTimeout(() => setAddedCrew(false), 2000);
    openCart();
  };

  const handleAddPavos = () => {
    addItem({
      id: 'recharge_vbucks_12500',
      offerId: 'recharge_vbucks_12500',
      name: '12.500 Pavos',
      vbucks: 12500,
      finalPrice: currency === 'MXN' ? 1099 : 89999,
      regularPrice: currency === 'MXN' ? 1099 : 89999,
      image: 'https://fortnite-api.com/images/vbuck.png',
      images: {
        icon: 'https://fortnite-api.com/images/vbuck.png',
        featured: 'https://fortnite-api.com/images/vbuck.png',
      },
      category: 'Recargas',
      rarity: 'Legendary',
      isBundle: false,
    });
    setAddedPavos(true);
    setTimeout(() => setAddedPavos(false), 2000);
    openCart();
  };

  const crewPrice = formatPrice(crewFiatNum, currency);
  const pavosPrice = currency === 'MXN' ? '$1.099 MXN' : '$89.999 ARS';

  return (
    <div className="w-full min-h-screen bg-transparent text-[#06173f] py-8 sm:py-12 px-4 sm:px-6">
      <div className="max-w-[1200px] mx-auto space-y-8">
        {/* 1. CABECERA DIRECTA 1:1 CON recargas.html */}
        <header className="space-y-1">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-[#06173f] font-heading">
            Recargas Directas
          </h1>
          <p className="text-sm sm:text-base text-[var(--soft)] font-medium max-w-2xl leading-relaxed">
            Las compras se realizan en la cuenta del cliente, tiempo de entrega en menos de 24hs
          </p>
        </header>

        {/* 2. GRID DE EXACTAMENTE 2 TARJETAS OFICIALES */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
          style={{ filter: 'drop-shadow(6px 6px 0 rgba(20, 71, 230, 0.14))' }}
        >
          {/* TARJETA 1: CLUB DE FORTNITE (CREW) */}
          <section
            id="crew"
            className="p-6 sm:p-7 bg-white border-2 border-[var(--line)] hover:border-[#1447e6] transition flex flex-col justify-between space-y-5"
            style={{ clipPath: 'var(--cut)' }}
          >
            <div>
              {/* Vitrina Club de Fortnite con layout 2 columnas y render dinámico de la skin del mes */}
              <div
                className="bg-[var(--ice)] p-4 sm:p-5 mb-4 relative overflow-hidden flex items-center justify-between min-h-[170px]"
                style={{ clipPath: 'var(--cut)' }}
              >
                <div className="relative z-10 flex flex-col gap-2 max-w-[62%]">
                  <span className="font-heading font-black text-xs sm:text-sm uppercase tracking-wider text-[#1447e6]">
                    {crewInfo.skin_name || 'Club de Fortnite'}
                  </span>
                  <div className="flex flex-col gap-1.5 items-start">
                    {crewInfo.extra_items && crewInfo.extra_items.length > 0 ? (
                      crewInfo.extra_items.map((it, idx) => (
                        <span
                          key={idx}
                          className="bg-white border border-[var(--line)] px-2.5 py-1 text-[11px] font-semibold text-[#06173f] flex items-center gap-1.5"
                          style={{ clipPath: 'var(--slash-sm)' }}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-[#1447e6] shrink-0" />
                          <span>{it.name}</span>
                        </span>
                      ))
                    ) : (
                      <>
                        <span
                          className="bg-white border border-[var(--line)] px-2.5 py-1 text-[11px] font-semibold text-[#06173f] flex items-center gap-1.5"
                          style={{ clipPath: 'var(--slash-sm)' }}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-[#1447e6] shrink-0" />
                          <span>Estilo LEGO®</span>
                        </span>
                        <span
                          className="bg-white border border-[var(--line)] px-2.5 py-1 text-[11px] font-semibold text-[#06173f] flex items-center gap-1.5"
                          style={{ clipPath: 'var(--slash-sm)' }}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-[#1447e6] shrink-0" />
                          <span>Accesorios del conjunto</span>
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Render recortado oficial de la skin del mes */}
                {crewInfo.skin_image && (
                  <div className="absolute -right-4 -bottom-2 top-0 w-44 flex items-end justify-center pointer-events-none z-0">
                    <img
                      src={crewInfo.skin_image}
                      alt={crewInfo.skin_name || 'Club de Fortnite'}
                      className="max-h-[190px] w-auto object-contain drop-shadow-[0_6px_14px_rgba(6,23,63,0.22)] transition-transform duration-300 hover:scale-105"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>

              <h2 className="text-2xl sm:text-3xl font-black uppercase text-[#06173f] font-heading mb-3">
                Club de Fortnite (Crew)
              </h2>

              <div className="flex flex-wrap gap-1.5 mb-4">
                <span className="text-xs font-semibold text-[#06173f] bg-[var(--ice)] px-2.5 py-1 flex items-center gap-1.5" style={{ clipPath: 'var(--slash-sm)' }}>
                  <Check className="w-3.5 h-3.5 text-[#1447e6]" />
                  <span>Pase de Batalla</span>
                </span>
                <span className="text-xs font-semibold text-[#06173f] bg-[var(--ice)] px-2.5 py-1 flex items-center gap-1.5" style={{ clipPath: 'var(--slash-sm)' }}>
                  <Coins className="w-3.5 h-3.5 text-[#1447e6]" />
                  <span>+800 Pavos</span>
                </span>
                <span className="text-xs font-semibold text-[#06173f] bg-[var(--ice)] px-2.5 py-1 flex items-center gap-1.5" style={{ clipPath: 'var(--slash-sm)' }}>
                  <Check className="w-3.5 h-3.5 text-[#1447e6]" />
                  <span>Skin + LEGO®</span>
                </span>
              </div>
            </div>

            <div className="pt-4 border-t-2 border-[var(--line)] flex flex-wrap items-center justify-between gap-3">
              <div>
                <span className="block font-heading font-black text-[11px] uppercase tracking-wider text-[var(--soft)]">
                  Precio del mes
                </span>
                <span className="font-heading font-black text-2xl sm:text-3xl text-[#1447e6]">
                  {crewPrice}
                </span>
              </div>
              <button
                type="button"
                onClick={handleAddCrew}
                className="btn px-5 py-3 bg-[#1447e6] hover:bg-[#0a2a9c] text-white font-heading font-black uppercase tracking-wider text-xs transition flex items-center gap-2 shadow-md cursor-pointer"
                style={{ clipPath: 'var(--slash)' }}
              >
                <ShoppingCart className="w-4 h-4" />
                <span>{addedCrew ? '¡Agregado!' : 'Agregar al carrito'}</span>
              </button>
            </div>
          </section>

          {/* TARJETA 2: 12.500 PAVOS (PAQUETE GIGANTE) */}
          <section
            id="pavos"
            className="p-6 sm:p-7 bg-white border-2 border-[var(--line)] hover:border-[#1447e6] transition flex flex-col justify-between space-y-5"
            style={{ clipPath: 'var(--cut)' }}
          >
            <div>
              <div
                className="bg-[var(--ice)] p-6 mb-4 flex flex-col items-center justify-center text-center min-h-[170px]"
                style={{ clipPath: 'var(--cut)' }}
              >
                <div
                  className="w-16 h-16 bg-[#1447e6] text-white flex items-center justify-center text-3xl shadow-md mb-1"
                  style={{ clipPath: 'var(--cut)' }}
                >
                  <Coins className="w-8 h-8 text-white" />
                </div>
              </div>

              <h2 className="text-2xl sm:text-3xl font-black uppercase text-[#06173f] font-heading mb-3">
                12.500 Pavos
              </h2>

              <div className="flex flex-wrap gap-1.5 mb-4">
                <span className="text-xs font-semibold text-[#06173f] bg-[var(--ice)] px-2.5 py-1 flex items-center gap-1.5" style={{ clipPath: 'var(--slash-sm)' }}>
                  <Check className="w-3.5 h-3.5 text-[#1447e6]" />
                  <span>Carga en el día</span>
                </span>
                <span className="text-xs font-semibold text-[#06173f] bg-[var(--ice)] px-2.5 py-1 flex items-center gap-1.5" style={{ clipPath: 'var(--slash-sm)' }}>
                  <Check className="w-3.5 h-3.5 text-[#1447e6]" />
                  <span>Todas las plataformas</span>
                </span>
                <span className="text-xs font-semibold text-[#06173f] bg-[var(--ice)] px-2.5 py-1 flex items-center gap-1.5" style={{ clipPath: 'var(--slash-sm)' }}>
                  <Check className="w-3.5 h-3.5 text-[#1447e6]" />
                  <span>+12 chances en Sorteo</span>
                </span>
              </div>
            </div>

            <div className="pt-4 border-t-2 border-[var(--line)] flex flex-wrap items-center justify-between gap-3">
              <div>
                <span className="block font-heading font-black text-[11px] uppercase tracking-wider text-[var(--soft)]">
                  Precio final
                </span>
                <span className="font-heading font-black text-2xl sm:text-3xl text-[#1447e6]">
                  {pavosPrice}
                </span>
              </div>
              <button
                type="button"
                onClick={handleAddPavos}
                className="btn px-5 py-3 bg-[#1447e6] hover:bg-[#0a2a9c] text-white font-heading font-black uppercase tracking-wider text-xs transition flex items-center gap-2 shadow-md cursor-pointer"
                style={{ clipPath: 'var(--slash)' }}
              >
                <ShoppingCart className="w-4 h-4" />
                <span>{addedPavos ? '¡Agregado!' : 'Agregar al carrito'}</span>
              </button>
            </div>
          </section>
        </div>

        {/* 3. TIRA MINIMALISTA DE ENTREGA EN 3 PASOS (.rec-flow) */}
        <section
          className="bg-white border-2 border-[var(--line)] p-4 sm:p-6 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 items-center"
          style={{ clipPath: 'var(--cut)' }}
          aria-label="Cómo funciona la entrega"
        >
          <div className="flex items-center gap-3">
            <span
              className="w-8 h-8 shrink-0 flex items-center justify-center bg-[var(--ice)] text-[#1447e6] font-heading font-black text-sm"
              style={{ clipPath: 'var(--slash)' }}
            >
              01
            </span>
            <div className="text-xs sm:text-sm text-[var(--soft)] leading-snug">
              <b className="block font-heading font-black text-[#06173f] text-xs sm:text-sm uppercase">
                Agregas al carrito
              </b>
              Eliges tu recarga y completas el pago en la web.
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span
              className="w-8 h-8 shrink-0 flex items-center justify-center bg-[var(--ice)] text-[#1447e6] font-heading font-black text-sm"
              style={{ clipPath: 'var(--slash)' }}
            >
              02
            </span>
            <div className="text-xs sm:text-sm text-[var(--soft)] leading-snug">
              <b className="block font-heading font-black text-[#06173f] text-xs sm:text-sm uppercase">
                Nos contactas
              </b>
              Un enlace te llevará a Whatsapp para coordinar la entrega.
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span
              className="w-8 h-8 shrink-0 flex items-center justify-center bg-[var(--ice)] text-[#1447e6] font-heading font-black text-sm"
              style={{ clipPath: 'var(--slash)' }}
            >
              03
            </span>
            <div className="text-xs sm:text-sm text-[var(--soft)] leading-snug">
              <b className="block font-heading font-black text-[#06173f] text-xs sm:text-sm uppercase">
                Aclaración
              </b>
              Ambas recargas requieren el acceso a tu cuenta de parte nuestra.
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default RecargasPage;
